import { useI18n } from '@i18n';
import {
  Avatar,
  Box,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Stack,
  Tooltip,
  useTheme,
} from '@mui/material';
import Divider from '@mui/material/Divider';
import { tooltipClasses } from '@mui/material/Tooltip';
import { role_enum } from '@predium/enums';
import { NavLink as RouterLink, useNavigate } from 'react-router-dom';
import { PTypography } from '@/components/atoms/Typography/PTypography';
import { useAccessToken } from '@/components/session/AccessTokenContext';
import { useSession } from '@/components/session/useSession';
import { ICONS } from '../../../assets/icons';
import GitBookLink from '../../../components/helpcenter/GitBookLink';
import LanguageSelect from '../../../components/i18n/LanguageSelect';
import Iconify from '../../../components/Iconify';
import MenuPopover from '../../../components/MenuPopover';
import { TileRole } from '../../../components/Tile';
import { PATHS } from '../../../routes';

type OrgConfig = {
  title: string;
  icon: string;
  path: string | null;
  role: TileRole;
  tile: string;
  allowedRoles: role_enum[];
  onClick?: () => void;
};

type OrgMenuProps = {
  openPopover: HTMLElement | null;
  handleClosePopover: VoidFunction;
  onNavItemClick?: VoidFunction;
};

export function OrgMenu({ openPopover, handleClosePopover, onNavItemClick }: OrgMenuProps) {
  const { t } = useI18n();
  const commitHash = import.meta.env.VITE_COMMIT_HASH;
  const { logout } = useAccessToken();
  const { org, user, isAdmin } = useSession();
  const navigate = useNavigate();
  const name = `${user.firstName} ${user.lastName}`;
  const initials = `${user.firstName[0] ?? ''}${user.lastName[0] ?? ''}`;
  const orgName = org.name;
  const userRole = user.role;
  const role = isAdmin ? userRole : role_enum.UNAUTHORIZED;
  const theme = useTheme();

  const handleProfileClick = () => {
    if (user?.id) {
      navigate(PATHS.org.user({ id: user.id }));
      handleClosePopover();
      onNavItemClick?.();
    }
  };

  const orgAllUsersConfigs: OrgConfig[] = [
    {
      title: t('PageName_ManageUsers'),
      icon: ICONS.MDI_ACCOUNT_GROUP_OUTLINE,
      path: PATHS.org.users(),
      role: 'button',
      tile: 'users',
      allowedRoles: [role_enum.ORG_ADMIN],
      onClick: () => {
        handleClosePopover();
        onNavItemClick?.();
      },
    },
    {
      title: t('Settings_ManageOrganization'),
      icon: ICONS.MATERIAL_SYMBOLS_TUNE,
      path: PATHS.org.settings(),
      role: 'button',
      tile: 'settings',
      allowedRoles: [role_enum.ORG_ADMIN],
      onClick: () => {
        handleClosePopover();
        onNavItemClick?.();
      },
    },
  ];
  const allowedOrgConfigs: OrgConfig[] = orgAllUsersConfigs.filter(
    //@ts-ignore
    (orgConfig) => orgConfig.allowedRoles.indexOf(role) !== -1,
  );
  const handleLogout = () => {
    logout();
  };

  return (
    <>
      <MenuPopover
        data-cy="cy-settings-menu"
        arrow="bottom-center"
        disabledArrow={true}
        open={openPopover}
        anchorEl={openPopover}
        onClose={handleClosePopover}
        handleClick={handleClosePopover}
        sx={{ width: 300, padding: 0, mt: -2 }}
      >
        <Stack direction="column" alignContent="flex-end">
          <Stack
            direction="column"
            alignContent="center"
            alignItems="center"
            sx={{
              py: 1,
              cursor: 'pointer',
              '&:hover': { bgcolor: theme.palette.grey[500_8] },
              transition: 'background-color 0.2s',
            }}
            onClick={handleProfileClick}
          >
            <Avatar
              sx={{
                bgcolor: 'warning.main',
                color: 'white',
                width: 48,
                height: 48,
                mb: 1,
                fontWeight: 700,
                fontSize: '14px',
              }}
              alt={name}
            >
              {initials}
            </Avatar>
            <PTypography variant="subtitle1">{name}</PTypography>
            <PTypography variant="body2" color="secondary">
              {orgName}
            </PTypography>
          </Stack>
          <Divider variant="fullWidth" sx={{ mx: 0.5 }} />
          <Box sx={{ width: '100%' }}>
            <List sx={{ padding: 0 }}>
              {allowedOrgConfigs.length > 0 && (
                <Stack>
                  {allowedOrgConfigs.map((orgConfig) => (
                    <ListItem
                      disablePadding
                      //@ts-ignore
                      component={RouterLink}
                      to={orgConfig.path}
                      sx={{
                        color: 'text.primary',
                        borderRadius: '6px',
                        width: 'calc(100% - 8px)',
                        margin: theme.spacing(0.5),
                      }}
                      key={orgConfig.title}
                    >
                      <ListItemButton data-cy={`cy-manage-${orgConfig.tile}-button-open`} onClick={orgConfig.onClick}>
                        <ListItemIcon>
                          <Iconify icon={orgConfig.icon} color="action.active" />
                        </ListItemIcon>
                        <ListItemText primary={orgConfig.title} />
                      </ListItemButton>
                    </ListItem>
                  ))}
                  <Divider variant="fullWidth" sx={{ mx: 0.5 }} />
                </Stack>
              )}
              <ListItem
                sx={{ width: 'calc(100% - 8px)', margin: theme.spacing(0.5), borderRadius: '6px' }}
                disablePadding
              >
                {/* Tooltip was used MAINLY for UX purposes. */}
                <Tooltip
                  title={
                    <LanguageSelect
                      handleParentMenuClose={() => {
                        handleClosePopover();
                        onNavItemClick?.();
                      }}
                    />
                  }
                  componentsProps={{
                    popper: {
                      sx: {
                        [`&.${tooltipClasses.popper}[data-popper-placement*="right"] .${tooltipClasses.tooltip}`]: {
                          marginLeft: '0px',
                        },
                      },
                    },
                    tooltip: {
                      sx: {
                        bgcolor: 'white',
                        p: 0,
                        borderRadius: 1.5,
                        boxShadow: theme.customShadows.dropdown,
                      },
                    },
                  }}
                  placement="right-start"
                  disableFocusListener
                >
                  <ListItemButton data-cy="cy-change-language-button-open">
                    <ListItemIcon>
                      <Iconify icon={ICONS.MDI_TRANSLATE} color="action.active" />
                    </ListItemIcon>
                    <ListItemText primary={t('Settings_Language')} />
                    <ListItemIcon sx={{ mr: 0 }}>
                      <Iconify icon={ICONS.CHEVRON_RIGHT} color="action.active" />
                    </ListItemIcon>
                  </ListItemButton>
                </Tooltip>
              </ListItem>
              {/* WIP in PRE-2100 */}
              <ListItem
                sx={{ width: 'calc(100% - 8px)', margin: '4px', borderRadius: '6px' }}
                disablePadding
                onClick={onNavItemClick}
              >
                <ListItemButton data-cy="cy-support-button-open" sx={{ px: 0 }}>
                  <GitBookLink showTooltip={false} sx={{ px: 2, width: '100%', '&:hover': { textDecoration: 'none' } }}>
                    <Stack direction="row" alignItems="center">
                      <ListItemIcon>
                        <Iconify icon={ICONS.HELP_CIRCLE} color="action.active" />
                      </ListItemIcon>
                      <ListItemText primary={t('Settings_Support')} />
                      {commitHash ? (
                        <PTypography variant="caption" color="secondary">
                          {t('Software_Version', { version: commitHash.slice(0, 7).toUpperCase() })}
                        </PTypography>
                      ) : null}
                    </Stack>
                  </GitBookLink>
                </ListItemButton>
              </ListItem>
              <Divider variant="fullWidth" sx={{ mx: 0.5 }} />
              <ListItem
                sx={{ width: 'calc(100% - 8px)', margin: theme.spacing(0.5), borderRadius: '6px' }}
                disablePadding
              >
                <ListItemButton data-cy="cy-logout-button" onClick={handleLogout}>
                  <ListItemIcon>
                    <Iconify icon={ICONS.MDI_LOGOUT} sx={{ color: 'red' }} />
                  </ListItemIcon>
                  <ListItemText primary={t('General_Logout')} sx={{ color: 'red' }} />
                </ListItemButton>
              </ListItem>
            </List>
          </Box>
        </Stack>
      </MenuPopover>
    </>
  );
}
